import type { FC, RefObject } from 'react'
import { alpha, Box, Stack, Typography, useTheme } from '@mui/material'
import { CloudUploadOutlined } from '@ant-design/icons'
import { motion } from 'framer-motion'

interface SmartImportDropZoneProps {
  isDragging: boolean
  onDragChange: (isDragging: boolean) => void
  onFileSelect: (file: File) => void
  fileInputRef: RefObject<HTMLInputElement | null>
}

const SmartImportDropZone: FC<SmartImportDropZoneProps> = ({
  isDragging,
  onDragChange,
  onFileSelect,
  fileInputRef,
}) => {
  const theme = useTheme()

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onDragChange(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onDragChange(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onDragChange(false)
    const file = e.dataTransfer.files[0]
    if (file) onFileSelect(file)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ width: '100%' }}
    >
      <Box
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          borderRadius: 4,
          border: '2px dashed',
          borderColor: isDragging ? theme.palette.primary.main : alpha(theme.palette.divider, 0.6),
          backgroundColor: isDragging
            ? alpha(theme.palette.primary.main, 0.04)
            : alpha(theme.palette.background.paper, 0.6),
          backdropFilter: 'blur(8px)',
          transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
          },
          '&:hover .drop-icon': {
            transform: 'scale(1.1)',
            color: theme.palette.primary.main,
          },
        }}
      >
        {/* Subtle corner accents */}
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            width: 24,
            height: 24,
            borderTop: `2px solid ${alpha(theme.palette.primary.main, isDragging ? 0.6 : 0.2)}`,
            borderLeft: `2px solid ${alpha(theme.palette.primary.main, isDragging ? 0.6 : 0.2)}`,
            borderRadius: '4px 0 0 0',
            transition: 'border-color 0.3s ease',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 24,
            height: 24,
            borderTop: `2px solid ${alpha(theme.palette.primary.main, isDragging ? 0.6 : 0.2)}`,
            borderRight: `2px solid ${alpha(theme.palette.primary.main, isDragging ? 0.6 : 0.2)}`,
            borderRadius: '0 4px 0 0',
            transition: 'border-color 0.3s ease',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            width: 24,
            height: 24,
            borderBottom: `2px solid ${alpha(theme.palette.primary.main, isDragging ? 0.6 : 0.2)}`,
            borderLeft: `2px solid ${alpha(theme.palette.primary.main, isDragging ? 0.6 : 0.2)}`,
            borderRadius: '0 0 0 4px',
            transition: 'border-color 0.3s ease',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            width: 24,
            height: 24,
            borderBottom: `2px solid ${alpha(theme.palette.primary.main, isDragging ? 0.6 : 0.2)}`,
            borderRight: `2px solid ${alpha(theme.palette.primary.main, isDragging ? 0.6 : 0.2)}`,
            borderRadius: '0 0 4px 0',
            transition: 'border-color 0.3s ease',
          }}
        />

        <Stack spacing={2} alignItems="center" sx={{ py: 4, px: 3 }}>
          <Box
            className="drop-icon"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: isDragging ? theme.palette.primary.main : theme.palette.text.secondary,
              transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            }}
          >
            <CloudUploadOutlined style={{ fontSize: 28 }} />
          </Box>

          <Stack spacing={0.5} alignItems="center" textAlign="center">
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDragging ? theme.palette.primary.main : theme.palette.text.primary,
                transition: 'color 0.3s ease',
              }}
            >
              {isDragging ? 'Drop your file here' : 'Drag & drop or click to browse'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              CSV, Excel, or PDF
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </motion.div>
  )
}

export default SmartImportDropZone
